import React, { useState } from "react";
import { ModuleItemInterface } from "../../../types/home.type";
import ImageComponent from "../ImageComponent";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectCoverflow } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";
import { Star } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import i18n from "../../../i18n";

interface PostTypeSliderSectionProps {
  module: ModuleItemInterface;
}

const coverflowEffects = {
  default: {
    rotate: 50,
    stretch: 0,
    depth: 100,
    modifier: 1,
    slideShadows: true,
  },
  dramatic: {
    rotate: 70,
    stretch: 0,
    depth: 200,
    modifier: 1.5,
    slideShadows: true,
  },
  subtle: {
    rotate: 30,
    stretch: 0,
    depth: 50,
    modifier: 0.7,
    slideShadows: false,
  },
  flat: {
    rotate: 0,
    stretch: 50,
    depth: 0,
    modifier: 1,
    slideShadows: false,
  },
};

const PostTypeSliderSection: React.FC<PostTypeSliderSectionProps> = ({
  module,
}) => {
  const [currentEffect, setCurrentEffect] = useState<string>("default");
 const navigate = useNavigate(); // Initialize navigate
  const { lang } = useParams<{ lang: string }>(); // Retrieve lang from URL
  
  return (
    <div className="post-type-slider-section overflow-hidden py-8 px-2 sm:py-12 sm:px-4">
      <Swiper
        modules={[Pagination, Autoplay, EffectCoverflow]}
        effect="coverflow"
        grabCursor={true}
        centeredSlides={true}
        // Responsive slidesPerView & spaceBetween
        slidesPerView={1}
        spaceBetween={10}
        breakpoints={{
          640: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 30,
          },
        }}
        initialSlide={1}
        loop={true}
        coverflowEffect={
          coverflowEffects[currentEffect as keyof typeof coverflowEffects]
        }
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
          bulletClass: "swiper-pagination-bullet !bg-red-500",
          bulletActiveClass: "swiper-pagination-bullet-active !bg-red-600",
        }}
        className="mySwiper py-6 sm:py-10 rounded-lg"
      >
        {Array.isArray(module.items) &&
          module.items.map((item: any) => (
            <SwiperSlide key={item.id} className="transition-all duration-300">
              {({ isActive }) => (
                <div
                  className={`relative group cursor-pointer transition-all duration-300 
                  ${
                    isActive
                      ? "sm:scale-125 z-10 rounded-lg overflow-hidden ring-offset-2 shadow-2xl"
                      : "scale-95 sm:scale-75 sm:opacity-50 opacity-100"
                  }`}
                >
                  {/* Banner Image */}
                  <ImageComponent
                    src={item.banner?.path || item.banner?.path || ""}
                    alt={item.title || "Untitled"}
                    className="w-full rounded-lg shadow-xl aspect-[16/9] object-cover"
                  />

                  {/* Banner Info Overlay */}
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent rounded-md flex flex-col justify-end p-4 sm:p-8 lg:p-16"
                    onClick={() => {
                      const type = item.posttype || "movie";
                      // Get current language from URL or fallback to i18n default language
                      const currentLang =
                        window.location.hash.split("/")[1] ||
                        lang ||
                        i18n.options.fallbackLng;
                      const path = `/${currentLang}/${type}/${item.slug}`;
                      navigate(path, { state: { item: item } });
                      window.scrollTo(0, 0);

                      // You can use a navigation function here if needed
                    }}
                  >
                    <h3 className="text-white text-lg sm:text-xl font-bold">
                      {item.title || "Untitled"}
                    </h3>
                    <div className="flex items-center">
                      <Star className="text-yellow-400 w-4 h-4 mr-1" />
                      <span className="text-white text-sm">{`${
                        (item.rating_total / item?.rating_count || 0).toFixed(
                          2
                        ) || 0
                      }/10 đánh giá`}</span>

                      {/* <LikeIcon className='text-yellow-400 w-4 h-4 ml-2 mr-1' /> */}
                      <img
                        src="/assets/icons/like-gold.svg"
                        alt="like"
                        className="w-4 h-4 ml-2 mr-1"
                      />
                      <span className="text-white text-sm">{`${
                        item.like_count || 0
                      } thích`}</span>
                    </div>
                  </div>
                </div>
              )}
            </SwiperSlide>
          ))}
      </Swiper>
    </div>
  );
};

export default PostTypeSliderSection;
